/**
 * @documental
 * @export Menu
 */
import { AriaAttributes, Children, FC, FocusEvent, KeyboardEvent, ReactNode, isValidElement, useCallback, useRef } from "react";

import { Body } from "../../typography";
import classNames, { Argument } from "classnames";

import { BaseProps } from "../../BaseProps";
import { createComponentStyleHook } from "../../tokens";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { BoundsAdjustmentCallback, Overlay } from "../Overlay";
import { ComponentItem, ComponentItemProps } from "./ComponentItem";
import { Group, GroupProps } from "./Group";
import { Item, ItemProps } from "./Item";
import { LinkItem, LinkItemProps } from "./LinkItem";
import { MenuCloseEventHandler } from "./MenuCloseEventHandler";
import * as Components from "./components";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/**
 * The menu styles.
 */
export const MenuItemStyles = tokenStyles;

/**
 * The `Menu` component properties.
 */
export interface MenuProps extends BaseProps {
  /** Element id for the menu container (useful for aria-controls). */
  readonly id?: string;
  /**
   * The CSS class name for the menu container. For more information on supported values, see the documentation for
   * the <a href="https://www.npmjs.com/package/classnames">classnames</a> library.
   */
  readonly containerClassName?: Argument;

  /**
   * Indicates whether the menu is visible.
   */
  readonly visible?: boolean;

  /**
   * The menu children.
   */
  readonly children?: ReactNode;

  /**
   * Called when the menu is attempting to close.
   */
  readonly onClose?: MenuCloseEventHandler;

  /**
   * Called when the bounds adjustment change is occurring.
   */
  readonly onBoundsChange?: BoundsAdjustmentCallback;

  /**
   * The id or space-separated list of element ids that describe the current element.
   */
  readonly "aria-describedby"?: AriaAttributes["aria-describedby"];
}

/**
 * The `Menu` component sub components.
 */
export interface MenuSubComponents {
  /**
   * Represents a single item placed within the Menu.
   */
  readonly Item: typeof Item;

  /**
   * Represents a single item placed within the Menu which when clicked navigates to a URL.
   */
  readonly LinkItem: typeof LinkItem;

  /**
   * Represents an item placed within the menu that can contain other React components.
   */
  readonly ComponentItem: typeof ComponentItem;

  /**
   * Represents a group of items.
   */
  readonly Group: typeof Group;
}

const useStyles = createComponentStyleHook<MenuProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: ["menu", "menu_item"],
});

const nativeKeyTags = ["INPUT", "TEXTAREA", "SELECT"];
const nativeKeyRoles = ["textbox", "searchbox", "combobox", "spinbutton"];

const handlesKeyNatively = (element: HTMLElement | null): boolean => {
  if (!element) return false;
  if (element.isContentEditable) return true;
  if (nativeKeyTags.includes(element.tagName)) return true;
  const role = element.getAttribute("role");
  return role !== null && nativeKeyRoles.includes(role);
};

/**
 * A menu which can be displayed attached to an HTML element or the mouse cursor.
 */
export const Menu = withSubComponents<FC<MenuProps>, MenuSubComponents>(
  {
    Item,
    LinkItem,
    ComponentItem,
    Group,
  },
  (props) => {
    const { containerClassName, visible = false, children, onClose, onBoundsChange, id, "aria-describedby": describedBy } = props;
    const { mode, styles, StyleModeBoundary } = useStyles(props);

    const menuRef = useRef<HTMLDivElement | null>(null);

    const htmlBaseProps = getHtmlPropsFromBaseProps(props, {
      className: [styles.Menu, "menu"],
      testIdPrefix: "menu",
    });

    const refHandler = useCallback((ref: HTMLDivElement | null) => {
      if (ref) ref.focus();
      menuRef.current = ref;
    }, []);

    const focusHandler = useCallback((ev: FocusEvent) => {
      ev.preventDefault();
    }, []);

    const endFocusHandler = useCallback((ev: FocusEvent) => {
      if (menuRef.current) {
        const item = menuRef.current.querySelector(".item:not(.disabled), .component-item");
        if (item && item instanceof HTMLElement) item.focus();
      }
      ev.preventDefault();
    }, []);

    const keyDownHandler = useCallback(
      (ev: KeyboardEvent) => {
        switch (ev.key) {
          case "Enter":
          case " ": {
            const focusedElement = document.activeElement;
            if (focusedElement instanceof HTMLElement) {
              if (handlesKeyNatively(focusedElement)) break;
              focusedElement.click();
            }
            ev.preventDefault();
            break;
          }
          case "ArrowDown": {
            if (menuRef.current) {
              const focusedElement = document.activeElement;
              const items = Array.from(menuRef.current.querySelectorAll(".item:not(.disabled), .component-item"));
              if (items.length === 0) return;

              if (focusedElement && focusedElement instanceof HTMLElement && menuRef.current.contains(focusedElement)) {
                const index = items.findIndex((i) => i === focusedElement);
                if (index >= 0) {
                  const nextIndex = (index + 1) % items.length;
                  const nextItem = items[nextIndex];
                  if (nextItem && nextItem instanceof HTMLElement) {
                    nextItem.focus();
                  }
                } else {
                  const firstItem = items[0];
                  if (firstItem && firstItem instanceof HTMLElement) firstItem.focus();
                }
              } else {
                const firstItem = items[0];
                if (firstItem && firstItem instanceof HTMLElement) firstItem.focus();
              }
            }
            ev.preventDefault();
            break;
          }
          case "ArrowUp": {
            if (menuRef.current) {
              const focusedElement = document.activeElement;
              const items = Array.from(menuRef.current.querySelectorAll(".item:not(.disabled), .component-item"));
              if (items.length === 0) return;

              if (focusedElement && focusedElement instanceof HTMLElement && menuRef.current.contains(focusedElement)) {
                const index = items.findIndex((i) => i === focusedElement);
                if (index >= 0) {
                  const nextIndex = (index - 1 + items.length) % items.length;
                  const nextItem = items[nextIndex];
                  if (nextItem && nextItem instanceof HTMLElement) nextItem.focus();
                } else {
                  const lastItem = items[items.length - 1];
                  if (lastItem && lastItem instanceof HTMLElement) lastItem.focus();
                }
              } else {
                const lastItem = items[items.length - 1];
                if (lastItem && lastItem instanceof HTMLElement) lastItem.focus();
              }
            }
            ev.preventDefault();
            break;
          }
          case "Home": {
            if (menuRef.current) {
              const items = Array.from(menuRef.current.querySelectorAll(".item:not(.disabled), .component-item"));
              if (items.length === 0) return;

              const firstItem = items[0];
              if (firstItem && firstItem instanceof HTMLElement) firstItem.focus();
            }
            ev.preventDefault();
            break;
          }
          case "End": {
            if (menuRef.current) {
              const items = Array.from(menuRef.current.querySelectorAll(".item:not(.disabled), .component-item"));
              if (items.length === 0) return;

              const lastItem = items[items.length - 1];
              if (lastItem && lastItem instanceof HTMLElement) lastItem.focus();
            }
            ev.preventDefault();
            break;
          }
          case "Escape": {
            onClose?.();
            ev.preventDefault();
            break;
          }
          default: {
            break;
          }
        }
      },
      [onClose],
    );

    if (!visible) return null;

    return (
      <StyleModeBoundary mode={mode}>
        <div className={classNames(styles.MenuContainer, "menu-container", "items", containerClassName)}>
          <Overlay adjust={onBoundsChange}>
            <div
              {...htmlBaseProps}
              id={id}
              role="menu"
              aria-describedby={describedBy}
              ref={refHandler}
              tabIndex={-1}
              onFocus={focusHandler}
              onKeyDown={keyDownHandler}
            >
              <Body>
                {Children.map(children, (child) => {
                  if (!isValidElement(child)) return null;

                  if (child.type === Item) {
                    const itemProps = child.props as ItemProps;
                    const { children } = itemProps;

                    return (
                      <Components.Item {...itemProps} onClose={onClose}>
                        {children}
                      </Components.Item>
                    );
                  } else if (child.type === LinkItem) {
                    const itemProps = child.props as LinkItemProps;
                    const { children } = itemProps;

                    return (
                      <Components.LinkItem {...itemProps} onClose={onClose}>
                        {children}
                      </Components.LinkItem>
                    );
                  } else if (child.type === ComponentItem) {
                    const componentItemProps = child.props as ComponentItemProps;
                    const { children } = componentItemProps;

                    return <Components.ComponentItem {...componentItemProps}>{children}</Components.ComponentItem>;
                  } else if (child.type === Group) {
                    const groupProps = child.props as GroupProps;
                    const { children } = groupProps;

                    return (
                      <Components.Group {...groupProps} onClose={onClose}>
                        {children}
                      </Components.Group>
                    );
                  }
                })}
                <div data-testid="menu-end-focus" onFocus={endFocusHandler} />
              </Body>
            </div>
          </Overlay>
        </div>
      </StyleModeBoundary>
    );
  },
);
